import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Exception } from '@cargos/sprintpay-models';
import {
    ErrorHandlerService,
    LoggerService,
    StorageService,
    StorageTypes,
    TokenService,
} from '@cargos/sprintpay-services';
import { deepSearchByKey } from '@cargos/sprintpay-tools';
import {
    BehaviorSubject,
    Observable,
    catchError,
    delay,
    filter,
    finalize,
    map,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { SessionService } from 'src/app/shared/services';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { CachedRequests, RetryRequestOptions } from '../models/errors';
import { ErrorHandler } from '../modules/cart/lib-cart/shared/services';
import { JwtHelperService } from './jwt-helper.service';

@Injectable({
    providedIn: 'root',
})
export class ErrorCustomHandlerService extends ErrorHandler implements ErrorHandlerService {
    private refreshTokenInProgress = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private excludedUrlsForUnauthorized: string[] = [];
    private retryRequestOptions: RetryRequestOptions = {
        retryDelay: 3000, //milliseconds
        requestDelay: 1000, //milliseconds
        maxAttempts: 2, //max number of retries
    };
    private cachedRequests = new Map<string, CachedRequests>();

    constructor(
        private router: Router,
        private sessionService: SessionService,
        private jwtHelperService: JwtHelperService,
        private tokenService: TokenService,
        private loggerService: LoggerService,
        private storageService: StorageService
    ) {
        super();
        this.excludedUrlsForUnauthorized = [
            `${environment.authenticationURL}`,
            `${environment.invoicesURL}/downloadInvoices`,
            `${environment.cartURL}/pay`,
            `${environment.cartURL}/startApprovalFlow`,
            `${environment.cartURL}/addPaymentRequest`,
            'api.ipify.org',
            '/create-payment',
        ];
    }

    handlerError(
        error: HttpErrorResponse | ErrorEvent | Exception | unknown,
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        if (error instanceof HttpErrorResponse) {
            let errorCode = (error as HttpErrorResponse).status;

            if (this.isExcludedUrlForUnauthorized(error.url || '')) {
                errorCode = 404;
            }

            if (!this.isRequestAllowedRetry(error.url || '')) {
                errorCode = 403;
            }

            switch (errorCode) {
                case 0:
                case 401:
                    return this.handlerUnauthorizedRequest(error, next, request);
                default:
                    return throwError(() => this.getErrors(error));
            }
        } else if (error?.['message']) {
            this.loggerService.captureException(new Error(error?.['message']), {
                tags: { errorEvent: true },
                extra: {
                    ...error,
                },
            });

            return throwError(() => Exception.fromJson({ description: error?.['message'] }));
        } else if (error instanceof Exception) {
            switch (error.code) {
                case 401:
                    this.loggerService.captureException(new Error(error?.description), {
                        tags: {
                            errorException: true,
                        },
                        extra: {
                            ...error,
                        },
                    });
                    return this.logoutGuestAndGetUnauthorizedError(error);
                default:
                    return throwError(() => error);
            }
        }

        this.loggerService.captureException(this.normalizeToError(error), {
            tags: {
                unknown: true,
            },
            extra: this.toErrorExtra(error),
        });

        return throwError(() => this.getErrors(error));
    }

    public normalizeToError(error: unknown): Error {
        const candidate = this.unwrapRejection(error);

        if (candidate instanceof Error) {
            return candidate;
        }

        if (typeof candidate === 'string') {
            return new Error(candidate);
        }

        if (candidate && typeof candidate === 'object') {
            const errorRecord = candidate as Record<string, unknown>;

            if (typeof errorRecord['message'] === 'string') {
                return new Error(errorRecord['message']);
            }

            if (typeof errorRecord['description'] === 'string') {
                return new Error(errorRecord['description']);
            }

            return new Error(this.extractMessageFromObject(errorRecord));
        }

        return new Error('Unknown Error');
    }

    private toErrorExtra(error: unknown): Record<string, unknown> {
        if (!error || typeof error !== 'object') {
            return { rawError: error };
        }

        return {
            ...(error as Record<string, unknown>),
        };
    }

    private unwrapRejection(error: unknown): unknown {
        if (error && typeof error === 'object' && 'rejection' in error) {
            return (error as Record<string, unknown>)['rejection'];
        }

        return error;
    }

    private extractMessageFromObject(errorObject: Record<string, unknown>): string {
        if (typeof errorObject['message'] === 'string' && errorObject['message'].length > 0) {
            return errorObject['message'];
        }

        if (typeof errorObject['description'] === 'string' && errorObject['description'].length > 0) {
            return errorObject['description'];
        }

        const status = typeof errorObject['status'] === 'number' ? `status=${errorObject['status']}` : '';
        const statusText = typeof errorObject['statusText'] === 'string' ? errorObject['statusText'] : '';
        const url = typeof errorObject['url'] === 'string' ? errorObject['url'] : '';

        const composed = [status, statusText, url].filter(Boolean).join(' | ');
        if (composed.length > 0) {
            return composed;
        }

        const keys = Object.keys(errorObject);
        return keys.length > 0 ? `Non-Error object thrown with keys: ${keys.join(', ')}` : 'Non-Error object thrown';
    }

    private handlerUnauthorizedRequest(
        error: HttpErrorResponse,
        next: HttpHandler,
        req: HttpRequest<unknown>
    ): Observable<HttpEvent<unknown>> {
        const userIsAuthenticated = this.sessionService.isAuthenticated();

        if (userIsAuthenticated) {
            return this.logoutAndGetUnauthorizedError(error);
        }

        if (this.sessionService.hasToken()) {
            return this.refreshTokenAndTryAgain(error, next, req);
        }

        return throwError(() => Exception.fromJson({}));
    }

    private createHandle(
        req: HttpRequest<unknown>,
        next: HttpHandler,
        newToken: string
    ): Observable<HttpEvent<unknown>> {
        const request = req.clone({
            setHeaders: {
                Authorization: `Bearer ${newToken}`,
            },
        });
        return next.handle(request).pipe(catchError((error) => this.logoutAndGetUnauthorizedError(error)));
    }

    private logoutAndGetUnauthorizedError(error: HttpErrorResponse): Observable<never> {
        return this.logoutUser().pipe(
            tap(() => {
                Swal.close();
                this.router.navigate(['/']);
            }),
            switchMap(() => {
                return throwError(() => this.getUnauthorizedError(error.url || ''));
            })
        );
    }

    private logoutGuestAndGetUnauthorizedError(error: HttpErrorResponse | Exception): Observable<never> {
        if (error instanceof Exception && error?.callback !== undefined) {
            this.reload(error.callback);
        }
        return this.logoutUser().pipe(
            tap(() => {
                Swal.close();
                this.router.navigate(['/']);
            }),
            switchMap(() => {
                return throwError(() => this.getGuestUnauthorizedError(error.url || ''));
            })
        );
    }

    private refreshToken(): Observable<string> {
        const refreshToken = this.getRefreshToken();
        const isTokenRefreshExpired = this.jwtHelperService.isTokenExpired(refreshToken);

        if (isTokenRefreshExpired) {
            return this.signAsGuest();
        }

        return this.sessionService.refreshToken().pipe(catchError(() => this.signAsGuest()));
    }

    private refreshTokenAndTryAgain(
        error: HttpErrorResponse,
        next: HttpHandler,
        req: HttpRequest<unknown>
    ): Observable<HttpEvent<unknown>> {
        if (this.refreshTokenInProgress) {
            const errorCode = (error as HttpErrorResponse).status;

            if (errorCode !== 0 && errorCode !== 401) {
                return this.refreshTokenSubject.pipe(
                    filter((result) => result !== null),
                    map((result) => result),
                    take(1),
                    switchMap(() => next.handle(req))
                );
            }
        } else if (this.isRequestAllowedRetry(req.url)) {
            this.refreshTokenInProgress = true;
            this.refreshTokenSubject.next(null);

            this.incrementRequestCount(req.url, new Date().valueOf());

            return this.refreshToken().pipe(
                switchMap((newToken) => {
                    this.refreshTokenSubject.next(newToken);
                    return this.createHandle(req, next, newToken).pipe(
                        map((response) => {
                            return response;
                        })
                    );
                }),
                delay(this.retryRequestOptions.requestDelay),
                finalize(() => (this.refreshTokenInProgress = false))
            );
        }
        return this.logoutGuestAndGetUnauthorizedError(error);
    }

    private signAsGuest(): Observable<string> {
        return this.sessionService.signInAsAGuest().pipe(map((tokens) => tokens && tokens.authorizationToken));
    }

    private logoutUser(): Observable<string> {
        return this.sessionService.logout(true).pipe(take(1));
    }

    parseAndReplaceError(error: unknown): unknown {
        if (!error || typeof error !== 'object') {
            return error;
        }

        try {
            const errorObject = error as { error?: string };
            if (errorObject.error && errorObject.error.trim().startsWith('{')) {
                const parsedError = JSON.parse(errorObject.error);
                errorObject.error = parsedError;
                return { ...errorObject };
            }
            return error;
        } catch (e) {
            return error;
        }
    }

    getErrors(error: HttpErrorResponse | unknown): Exception {
        let errors: string[] = [];

        error = this.parseAndReplaceError(error);

        if (
            error &&
            error?.['error'] &&
            error?.['error']?.error &&
            error?.['error']?.error?.title &&
            error?.['error']?.error?.body
        ) {
            errors = [error?.['error']?.error?.body];
        } else {
            errors = deepSearchByKey<HttpErrorResponse | unknown>(error, 'errors');
        }

        const url = deepSearchByKey<HttpErrorResponse | unknown>(error, 'url');

        const title = deepSearchByKey<HttpErrorResponse | unknown>(error, 'title');

        return Exception.fromJson({
            code: error?.['status'],
            description: !!errors.length ? errors.join(',') : undefined,
            url: url[0] || '',
            title: Array.isArray(title) && title[0] ? title[0] : 'Oops!',
        });
    }

    private isExcludedUrlForUnauthorized(requestUrl: string): boolean {
        for (let url of this.excludedUrlsForUnauthorized) {
            if (new RegExp(url).test(requestUrl) || requestUrl.endsWith(url)) {
                return true;
            }
        }
        return false;
    }

    private getUnauthorizedError(url: string): Exception {
        return Exception.fromJson({
            code: 401,
            title: 'Your session expired',
            description: 'Please try again',
            url,
        });
    }

    private getGuestUnauthorizedError(url: string): Exception {
        return Exception.fromJson({
            code: 401,
            title: 'We are having problems getting your information',
            description: 'Please try again',
            url,
        });
    }

    private getRefreshToken(): string {
        return this.tokenService.instant_refresh_token;
    }

    private incrementRequestCount(url: string, timestamp: number): void {
        if (this.cachedRequests.has(url)) {
            const currentCount = this.getRequestCount(url);
            this.cachedRequests.set(url, {
                count: currentCount + 1,
                lastTimestamp: timestamp,
            });

            return;
        }

        this.cachedRequests.set(url, {
            count: 0,
            lastTimestamp: timestamp,
        });
    }

    private getRequestCount(url: string): number {
        return this.cachedRequests.get(url)?.count ?? 0;
    }

    private isRequestAllowedRetry(url: string): boolean {
        return this.getRequestCount(url) < this.retryRequestOptions.maxAttempts;
    }

    private reload(callback: () => void): void {
        const maxAttempts = 2;
        const attempts = Number(this.storageService.getElement('reloadAttempts', StorageTypes.SESSION) ?? 0);

        if (attempts >= maxAttempts) {
            return;
        }

        this.storageService.saveElement('reloadAttempts', `${attempts + 1}`, StorageTypes.SESSION);
        callback();
    }

    getErrorMsg(error: unknown): string {
        if (error instanceof HttpErrorResponse) {
            return error.message || 'Unknown HTTP error';
        }
        return error instanceof Error ? error.message : String(error);
    }

    getHttpErrorResponse(httpError: HttpErrorResponse): string {
        return httpError.error?.message || httpError.message || 'Unknown error';
    }
}
